// useDecisions — Phase 9 damage / refund / reprint queue.
//
// Each conversation may have zero or more pending decision rows in
// `decisions_queue`, inserted by the proposer when an intent like 'damage'
// or 'cancel' fires. The Command Center renders a small panel for any
// pending rows; clicking Approve / Deny POSTs to the resolve route which
// flips the status without performing the actual Shopify or ShipStation
// mutation (per the read-only Phase 8 policy — Kimi still completes the
// real work in the destination tool).
//
// Pattern matches useCustomerHistory: keyed by conversation id, gentle
// staleTime, invalidate the conversation key on resolve so the panel
// updates instantly.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost } from '@/lib/api';

export type DecisionKind =
  | 'refund'
  | 'reprint'
  | 'address_change_review'
  | 'cancel_review';

export type DecisionStatus = 'pending' | 'approved' | 'denied' | 'cancelled';

export type DecisionRow = {
  id: string;
  conversation_id: string;
  kind: DecisionKind;
  order_name: string | null;
  note: string | null;
  status: DecisionStatus;
  resolved_at: string | null;
  created_at: string;
};

type ListResponse = { ok: true; decisions: DecisionRow[] };
type ResolveResponse = {
  ok: true;
  decision: Pick<DecisionRow, 'id' | 'conversation_id' | 'kind' | 'order_name' | 'status' | 'resolved_at'>;
};

function key(conversationId: string | null | undefined) {
  return ['decisions', conversationId ?? 'none'] as const;
}

export function useDecisions(conversationId: string | null | undefined) {
  return useQuery({
    queryKey: key(conversationId),
    enabled: !!conversationId,
    staleTime: 30_000,
    queryFn: async (): Promise<DecisionRow[]> => {
      if (!conversationId) return [];
      const res = await apiGet<ListResponse>(
        `/api/decisions?conversation_id=${encodeURIComponent(conversationId)}`,
      );
      return res.decisions;
    },
  });
}

export function useResolveDecision(conversationId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; status: Exclude<DecisionStatus, 'pending'>; note?: string }) => {
      const res = await apiPost<ResolveResponse, { status: DecisionStatus; note?: string }>(
        `/api/decisions/${encodeURIComponent(vars.id)}/resolve`,
        { status: vars.status, note: vars.note },
      );
      return res.decision;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: key(conversationId) });
    },
  });
}

// Static labels for each decision kind. Lifts the chip text out of JSX so
// new kinds added on the backend just need an entry here. Verb is what the
// approve button says; subject is what the chip / heading row says.
export const DECISION_META: Record<
  DecisionKind,
  { chipLabel: string; verb: string; help: string }
> = {
  refund: {
    chipLabel: 'Refund pending',
    verb: 'Approve refund',
    help: "Marks the decision as approved here. You'll still need to issue the refund in Shopify or Etsy.",
  },
  reprint: {
    chipLabel: 'Reprint pending',
    verb: 'Approve reprint',
    help: 'Marks the decision as approved here. Drop a duplicate order into ShipStation when ready.',
  },
  address_change_review: {
    chipLabel: 'Address change',
    verb: 'Approve change',
    help: 'Marks the decision as approved here. Update the address in ShipStation before the order ships.',
  },
  cancel_review: {
    chipLabel: 'Cancel request',
    verb: 'Approve cancel',
    help: 'Marks the decision as approved here. Cancel the order in Shopify or ShipStation manually.',
  },
};
